import React, { isValidElement, useMemo, useState, useCallback, useEffect } from 'react';
import { safeRest, defaultBaseline, uniqueId } from '@wds/utils';
import { Label } from '@wds/label';
import { IconCheck, IconCaretDown } from '@wds/icons';
import { Menu } from '@wds/menu';
import { createMemoStyles } from '@wds/styles';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function () {
  __assign = Object.assign || function __assign(t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
function __rest(s, e) {
  var t = {};
  for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];
  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
}
var SelectOption = function (_a) {
  var children = _a.children,
    _b = _a.disabled,
    disabled = _b === void 0 ? false : _b,
    value = _a.value;
  _a.selectedValue;
  var rest = __rest(_a, ["children", "disabled", "value", "selectedValue"]);
  return React.createElement("div", __assign({
    "data-disabled": disabled,
    "data-value": value
  }, safeRest(rest)), children);
};
var useStyles = createMemoStyles(function (theme) {
  var palette = theme.palette,
    border = theme.border,
    shadow = theme.shadow,
    spacing = theme.spacing,
    radius = theme.radius,
    type = theme.type;
  var select = {
    height: {
      sm: 32,
      md: 40,
      lg: 48
    },
    borderRadius: radius.small,
    iconColor: palette.muted.contrast,
    fontSize: type.fontSize.body
  };
  var selectMenuStyles = {
    wrenchSelectMenuContainer: function (_a) {
      var color = _a.color,
        size = _a.size,
        disabled = _a.disabled,
        stretched = _a.stretched,
        noBorder = _a.noBorder;
      return {
        position: 'relative',
        display: 'inline-block',
        cursor: 'pointer',
        boxSizing: 'border-box',
        backgroundColor: palette.background.light,
        border: noBorder ? 'none' : border.main,
        borderColor: color ? palette[color].main : null,
        fontSize: select.fontSize,
        color: palette.text.dark,
        borderRadius: select.borderRadius,
        pointerEvents: disabled ? 'none' : null,
        opacity: disabled ? '0.4' : null,
        width: stretched ? '100%' : null,
        maxWidth: '100%',
        height: size ? select.height[size] : null,
        flexGrow: '1',
        '&:hover': {
          borderColor: color ? palette[color].main : palette.border.hover,
          boxShadow: shadow.sky,
          transition: ['all', '100ms']
        },
        '&:focus, &:focus-within': {
          borderColor: color ? palette[color].main : palette.border.active,
          transition: ['border-color', '100ms'],
          outline: '0'
        },
        '& > span': {
          position: 'initial',
          flexGrow: '1',
          width: '100%',
          '& > div > [role="listbox"]': {
            maxWidth: stretched ? '100%' : 'none',
            marginTop: 1
          }
        },
        '& > span > [role="listbox"] [role="option"] > span': {
          pointerEvents: 'none',
          '& > svg': {
            marginRight: spacing[2]
          }
        },
        '& > span > [role="listbox"] [role="option"] > div': {
          pointerEvents: 'none' // don't steal click event from parent
        }
      };
    },

    wrenchSelectMenu: function (_a) {
      var size = _a.size,
        addOn = _a.addOn;
      return __assign(__assign({}, defaultBaseline), {
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        height: size ? select.height[size] : null,
        fontSize: select.fontSize,
        cursor: 'pointer',
        padding: [0, spacing[7], 0, spacing[4]],
        paddingLeft: addOn ? "".concat(spacing[6] + spacing[2], "px") : null
      });
    },
    wrenchSelectMenuSelectedItem: function () {
      return {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        '& > svg': {
          marginRight: spacing[2]
        }
      };
    },
    wrenchSelectMenuNonSelectedItemLeft: function () {
      return {
        width: spacing[4],
        height: spacing[4]
      };
    },
    wrenchSelectMenuAffordance: function () {
      return {
        position: 'absolute',
        top: '50%',
        right: spacing[4],
        transform: 'translateY(-0.75em)',
        pointerEvents: 'none',
        width: '1em',
        height: '1em',
        '& > svg': {
          fill: select.iconColor
        }
      };
    },
    wrenchSelectMenuAddOn: function () {
      return __assign(__assign({}, defaultBaseline), {
        fontFamily: type.fontFamily.base,
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-0.75em)',
        left: 16,
        paddingRight: spacing[3],
        pointerEvents: 'none'
      });
    }
  };
  return selectMenuStyles;
});
var SelectMenuItem = React.memo(function SelectMenuItem(_a) {
  var child = _a.child,
    hasSelectedValue = _a.hasSelectedValue,
    id = _a.id,
    index = _a.index,
    handleMenuClose = _a.handleMenuClose,
    onClick = _a.onClick,
    selected = _a.selected,
    selectedPlacement = _a.selectedPlacement,
    wrenchSelectMenuNonSelectedItemLeft = _a.wrenchSelectMenuNonSelectedItemLeft;
  var menuItemId = "select-child-".concat(index);
  if (isValidElement(child)) {
    var _b = child.props,
      childsChildren = _b.children,
      icon = _b.icon,
      childValue = _b.value;
    return React.createElement(Menu.Item, {
      key: id,
      id: menuItemId,
      disabled: child.props.disabled,
      "data-value": childValue,
      handleMenuClose: handleMenuClose,
      onClick: onClick,
      addOn: selected ? React.createElement("span", {
        className: "select__checked"
      }, React.createElement(IconCheck, {
        title: "Check mark icon",
        color: "success"
      })) : hasSelectedValue && selectedPlacement === 'left' && React.createElement("span", {
        className: wrenchSelectMenuNonSelectedItemLeft
      }),
      addOnPlacement: selectedPlacement,
      role: "option"
    }, icon, childsChildren);
  }
  // return null here for linting rule requirements
  return React.createElement(React.Fragment, null, null);
});
var SelectedItem = function (_a) {
  var children = _a.children,
    selectedValue = _a.selectedValue;
  if (selectedValue) {
    return React.createElement(React.Fragment, null, selectedValue);
  }
  if (isValidElement(children) && children.props) {
    var displayItem = children.props.children;
    return React.createElement(React.Fragment, null, displayItem);
  }
  return React.createElement(React.Fragment, null, children);
};
var SelectMenu = function (_a) {
  var children = _a.children,
    id = _a.id,
    label = _a.label,
    _b = _a.placeholder,
    placeholder = _b === void 0 ? 'Choose...' : _b,
    ariaLabel = _a.ariaLabel,
    _c = _a.disabled,
    disabled = _c === void 0 ? false : _c,
    color = _a.color,
    value = _a.value,
    _d = _a.size,
    size = _d === void 0 ? 'md' : _d,
    _e = _a.stretched,
    stretched = _e === void 0 ? false : _e,
    _f = _a.noBorder,
    noBorder = _f === void 0 ? false : _f;
  _a.name;
  var onChange = _a.onChange,
    addOn = _a.addOn,
    _g = _a.selectedPlacement,
    selectedPlacement = _g === void 0 ? 'left' : _g,
    _h = _a.placement,
    placement = _h === void 0 ? 'bottom-left' : _h,
    renderTo = _a.renderTo,
    elevation = _a.elevation,
    _j = _a.alwaysRenderContent,
    alwaysRenderContent = _j === void 0 ? false : _j,
    rest = __rest(_a, ["children", "id", "label", "placeholder", "ariaLabel", "disabled", "color", "value", "size", "stretched", "noBorder", "name", "onChange", "addOn", "selectedPlacement", "placement", "renderTo", "elevation", "alwaysRenderContent"]);
  var options = useMemo(function () {
    return React.Children.toArray(children);
  }, [children]);
  var menuID = useMemo(function () {
    return uniqueId('input');
  }, []);
  var inputID = useMemo(function () {
    return id !== null && id !== void 0 ? id : menuID;
  }, [id, menuID]);
  var _k = useState(false),
    showMenu = _k[0],
    setShowMenu = _k[1];
  var _l = useState(placeholder),
    selectedItem = _l[0],
    setSelectedItem = _l[1];
  var _m = useState(value),
    selectedValue = _m[0],
    setSelectedValue = _m[1];
  var _o = useStyles({
      size: size,
      color: color,
      disabled: disabled,
      stretched: stretched,
      addOn: addOn,
      noBorder: noBorder
    }),
    wrenchSelectMenuNonSelectedItemLeft = _o.wrenchSelectMenuNonSelectedItemLeft,
    wrenchSelectMenu = _o.wrenchSelectMenu,
    wrenchSelectMenuSelectedItem = _o.wrenchSelectMenuSelectedItem,
    wrenchSelectMenuAffordance = _o.wrenchSelectMenuAffordance,
    wrenchSelectMenuContainer = _o.wrenchSelectMenuContainer,
    wrenchSelectMenuAddOn = _o.wrenchSelectMenuAddOn;
  var handleMenuChange = useCallback(function () {
    setShowMenu(false);
  }, []);
  var updateValues = useCallback(function (updatedValue) {
    setSelectedValue(updatedValue);
    var item = options.find(function (child) {
      var _a;
      if (isValidElement(child) && updatedValue) {
        return (_a = child.props.value === updatedValue.toString()) !== null && _a !== void 0 ? _a : child;
      }
      return null;
    });
    if (isValidElement(item)) {
      var newSelectedItem = React.createElement(SelectedItem, __assign({}, item.props));
      setSelectedItem(newSelectedItem);
    }
  }, [options]);
  useEffect(function () {
    if (value !== undefined) {
      updateValues(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  var clickMenuItem = useCallback(function (e) {
    var el = e.currentTarget;
    var val = el.dataset.value;
    e.persist();
    // Only if the component is uncontrolled, should we update the internal value state.
    if (value === undefined) {
      updateValues(val);
      handleMenuChange();
    }
    if (onChange) {
      onChange(e);
    }
  }, [handleMenuChange, onChange, updateValues, value]);
  var menuItems = React.Children.map(options, function (child, index) {
    if (isValidElement(child)) {
      return React.createElement(SelectMenuItem, {
        child: child,
        hasSelectedValue: Boolean(selectedValue),
        id: id,
        index: index,
        onClick: clickMenuItem,
        selected: selectedValue === child.props.value,
        selectedPlacement: selectedPlacement,
        wrenchSelectMenuNonSelectedItemLeft: wrenchSelectMenuNonSelectedItemLeft
      });
    }
    return null;
  });
  var selectMenu = React.createElement("div", {
    className: wrenchSelectMenu
  }, React.createElement("div", {
    className: wrenchSelectMenuSelectedItem
  }, selectedItem), React.createElement("div", {
    className: wrenchSelectMenuAffordance
  }, React.createElement(IconCaretDown, {
    title: "Dropdown icon"
  })));
  return React.createElement(React.Fragment, null, !!label && React.createElement(Label, {
    htmlFor: inputID
  }, label), React.createElement("div", __assign({
    id: inputID,
    className: wrenchSelectMenuContainer,
    tabIndex: -1,
    role: "combobox",
    "aria-haspopup": "listbox",
    "aria-label": ariaLabel !== null && ariaLabel !== void 0 ? ariaLabel : 'Select Menu',
    "aria-controls": menuID,
    "aria-expanded": showMenu
  }, safeRest(rest)), addOn && React.createElement("div", {
    className: wrenchSelectMenuAddOn
  }, addOn), React.createElement(Menu, {
    dense: true,
    noRules: true,
    id: menuID,
    menuTrigger: selectMenu,
    placement: placement,
    show: showMenu,
    onChange: handleMenuChange,
    role: "listbox",
    renderTo: renderTo,
    elevation: elevation,
    stretched: stretched,
    alwaysRenderContent: alwaysRenderContent
  }, menuItems)));
};
SelectMenu.Option = SelectOption;
export { SelectMenu };